import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { API_BASE_URL } from '../../utils/api'
import { BaseWidget } from './BaseWidget'
import { DollarIcon, TrendingUpIcon, FileText } from '../Icons'

interface PipelineValueWidgetProps {
  className?: string
  widgetId?: string
}

interface BankPercentage {
  bank: string
  count: number
  percentage: string
}

interface DashboardStats {
  totalCount: number
  totalFinancingAmount: number
  totalNetCommission: number
  averageLoanAmount: number
  bankPercentages: BankPercentage[]
}

const PipelineValueWidget: React.FC<PipelineValueWidgetProps> = ({ className, widgetId }) => {
  const { t } = useTranslation()
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [isMinimal, setIsMinimal] = useState(false)

  // Detect widget height for minimal display
  useEffect(() => {
    const checkHeight = () => {
      const widgetElement = document.querySelector(`[data-widget-id="${widgetId || 'pipeline_value'}"]`)
      if (widgetElement) {
        const height = widgetElement.clientHeight
        // Trigger minimal display at 340px or less (h:2 grid units)
        setIsMinimal(height <= 340)
      }
    }
    
    checkHeight()
    // Add a small delay to ensure proper rendering
    const timer = setTimeout(checkHeight, 100)
    window.addEventListener('resize', checkHeight)
    const observer = new MutationObserver(checkHeight)
    const widgetElement = document.querySelector(`[data-widget-id="${widgetId || 'pipeline_value'}"]`)
    if (widgetElement) {
      observer.observe(widgetElement, { attributes: true, attributeFilter: ['style'] })
    }
    
    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', checkHeight)
      observer.disconnect()
    }
  }, [widgetId])

  const formatAmount = (amount: number) => {
    if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(1)}M$`
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(0)}K$`
    }
    return `$${amount.toLocaleString()}`
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await fetch(`${API_BASE_URL}/api/deals/pipeline-metrics`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (response.ok) {
        const data = await response.json()
        setStats(data)
      }
    } catch (error) {
      console.error('Error fetching pipeline stats:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStats()
  }, [])

  return (
    <BaseWidget
      title={t('dashboard.widgets.pipelineStatistics')}
      icon={<TrendingUpIcon className="h-5 w-5" />}
      accent="analytics"
      variant="gradient"
      className={className}
      loading={loading || !stats}
      minimalDisplay={isMinimal}
    >
      {isMinimal ? (
        // Minimal display - optimized layout with larger text
        <div className="space-y-3 h-full flex flex-col justify-center py-2">
          {/* Top row - Active Files & Average */}
          <div className="grid grid-cols-2 gap-3">
            <div className="text-center bg-white/10 rounded-lg py-3">
              <div className="text-3xl font-bold text-white tabular-nums">{stats?.totalCount || 0}</div>
              <div className="text-sm text-white/90 mt-1 font-medium uppercase tracking-wide">{t('dashboard.widgets.activeFiles')}</div>
            </div>
            <div className="text-center bg-white/10 rounded-lg py-3">
              <div className="text-3xl font-bold text-white tabular-nums">{formatAmount(stats?.averageLoanAmount || 0)}</div>
              <div className="text-sm text-white/90 mt-1 font-medium uppercase tracking-wide">{t('dashboard.widgets.averageLoan')}</div>
            </div>
          </div>
          {/* Bottom row - Total (Featured) */}
          <div className="text-center bg-white/15 rounded-lg py-4">
            <div className="text-4xl font-bold text-white tabular-nums">
              {formatAmount(stats?.totalFinancingAmount || 0)}
            </div>
            <div className="text-base text-white/90 mt-2 font-medium uppercase tracking-wide">
              {t('dashboard.widgets.totalFinancingAmount')}
            </div>
          </div>
        </div>
      ) : (
        // Full display
        <div className="space-y-4">
          {/* Top Metrics Grid */}
          <div className="grid grid-cols-2 gap-3">
            {/* Active Files */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
              <div className="flex items-center space-x-2 mb-1">
                <FileText className="h-4 w-4 text-white/70" />
                <p className="text-xs text-white/70">{t('dashboard.widgets.activeFiles')}</p>
              </div>
              <p className="text-2xl font-bold text-white tabular-nums">{stats?.totalCount || 0}</p>
            </div>

            {/* Average Loan */}
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
              <div className="flex items-center space-x-2 mb-1">
                <DollarIcon className="h-4 w-4 text-white/70" />
                <p className="text-xs text-white/70">{t('dashboard.widgets.averageLoan')}</p>
              </div>
              <p className="text-2xl font-bold text-white tabular-nums">
                {formatAmount(stats?.averageLoanAmount || 0)}
              </p>
            </div>
          </div>

          {/* Total Financing - Prominent */}
          <div className="bg-white/15 backdrop-blur-sm rounded-lg p-4 text-center">
            <p className="text-sm text-white/80 mb-1">{t('dashboard.widgets.totalFinancingAmount')}</p>
            <h2 className="text-4xl font-bold text-white tabular-nums">
              {formatAmount(stats?.totalFinancingAmount || 0)}
            </h2>
          </div>

          {/* Expected Commission */}
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
            <p className="text-xs text-white/70 mb-1">{t('dashboard.widgets.expectedCommission')}</p>
            <p className="text-xl font-semibold text-white tabular-nums">
              {formatCurrency(stats?.totalNetCommission || 0)}
            </p>
          </div>

          {/* Bank Distribution */}
          {stats?.bankPercentages && stats.bankPercentages.length > 0 && (
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
              <p className="text-xs text-white/70 mb-3 font-semibold">{t('dashboard.widgets.bankDistribution')}</p>
              <div className="space-y-2">
                {stats.bankPercentages.slice(0, 5).map((bank, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                      <div className="w-1.5 h-1.5 rounded-full bg-white/80 flex-shrink-0" />
                      <span className="text-sm text-white truncate">{bank.bank}</span>
                    </div>
                    <div className="flex items-center space-x-3 flex-shrink-0 ml-2">
                      <span className="text-sm text-white/60">{bank.count}</span>
                      <span className="text-sm font-semibold text-white min-w-[45px] text-right">
                        {bank.percentage}%
                      </span>
                    </div>
                  </div>
                ))}
                {stats.bankPercentages.length > 5 && (
                  <p className="text-xs text-white/50 text-center mt-2">
                    +{stats.bankPercentages.length - 5} {t('dashboard.widgets.moreBanks')}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </BaseWidget>
  )
}

export default PipelineValueWidget